import {
  absoluteImage,
  absoluteUrl,
  DEFAULT_SEO_PUBLISHER,
  stripHtml,
} from "../config/seo";

const truncate = (s: string, maxLen: number) => {
  const text = s.trim();
  if (!text) return "";
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trim()}...`;
};

type AudioType = "audios" | "pravachans" | "DailySatsang";

const TYPE_LABELS: Record<AudioType, string> = {
  audios: "Audio",
  pravachans: "Pravachan",
  DailySatsang: "Daily Satsang",
};

const TYPE_LABELS_HI: Record<AudioType, string> = {
  audios: "ऑडियो",
  pravachans: "प्रवचन",
  DailySatsang: "दैनिक सत्संग",
};

export const buildAudioDescription = (audio: any, type: AudioType) => {
  const name = audio?.name ? String(audio.name).trim() : "";
  const author = audio?.author ? String(audio.author).trim() : "";
  const label = TYPE_LABELS[type] || "Audio";

  if (name && author) {
    return truncate(`Listen to ${name} by ${author} on Gita Seva. ${label} for spiritual growth.`, 160);
  }
  if (name) {
    return truncate(`Listen to ${name} on Gita Seva. ${label} for spiritual growth and devotion.`, 160);
  }
  return `Listen to ${label} on Gita Seva.`;
};

export const buildAudioKeywords = (audio: any, type: AudioType) => {
  const name = audio?.name ? String(audio.name).trim() : "";
  const author = audio?.author ? String(audio.author).trim() : "";
  const label = TYPE_LABELS[type] || "Audio";

  const parts = [name, author, label, "Gita Seva", "Hindu", "Devotional", "Spiritual"]
    .filter(Boolean);

  const uniq: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (!uniq.some((x) => x.toLowerCase() === key)) uniq.push(p);
  }

  return uniq.join(", ");
};

export const buildAudioJsonLd = (audio: any, canonicalPath: string, type: AudioType) => {
  const url = absoluteUrl(canonicalPath);
  const name = audio?.name ? String(audio.name).trim() : "";
  const authorName = audio?.author ? String(audio.author).trim() : "";
  const description = buildAudioDescription(audio, type);

  // Use ogImage only - it is the dedicated OG image, different from audio thumbnail
  const image = audio?.ogImage ? absoluteImage(audio.ogImage) : "";

  const jsonLd: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "AudioObject",
    name: name || undefined,
    url: url || undefined,
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
    ...(authorName ? { author: { "@type": "Person", name: authorName } } : {}),
    publisher: { "@type": "Organization", name: DEFAULT_SEO_PUBLISHER },
    ...(audio?.ctime ? { datePublished: new Date(audio.ctime).toISOString() } : {}),
    inLanguage: "hi",
    genre: type === "pravachans" ? "Spiritual Discourse" : type === "DailySatsang" ? "Daily Satsang" : "Devotional Music",
  };

  // Remove undefined properties so schema is cleaner.
  for (const key of Object.keys(jsonLd)) {
    if (jsonLd[key] === undefined) delete jsonLd[key];
  }

  return jsonLd;
};

export const getAudioTypeLabel = (type: AudioType): string => {
  return TYPE_LABELS[type] || "Audio";
};

export const getAudioTypeLabelHi = (type: AudioType): string => {
  return TYPE_LABELS_HI[type] || "ऑडियो";
};
